import { useEffect, useRef, useCallback } from "react";
import { useGameSounds } from "@educaplay/core";
import {
  CIRCLE_RADIUS,
  LINE_THICKNESS,
  RELACIONAR_COLUMNAS_SOUNDS,
} from "../../utils/constants";
import styles from "./DrawingArea.module.scss";
import AnimatedLine from "../AnimatedLine";
import { useDrag } from "../../contexts/DragContext";
import { useMatch } from "../../contexts/MatchContext";
import { getLength } from "../../utils/getLength";
import { useAutoScroll } from "../../hooks/useAutoScroll";

export function DrawingArea({ canvasRef, scrollContainerRef, children, onDragMatch }) {
  const containerRef = useRef(null);
  const lineRef = useRef(null);

  const gameSounds = useGameSounds();
  const {
    isDragging,
    dragLine,
    startCard,
    startDrag,
    updateDragLine,
    updateDragStart,
    endDrag,
  } = useDrag();
  const { arrows, getCirclePosition, selectLeft, selectRight, clearSelection } =
    useMatch();

  useAutoScroll(containerRef, isDragging, dragLine);

  const getCardFromElement = useCallback((element) => {
    if(!element) return null;
    const button = element.closest("[data-card-meta]");
    if (!button || !button.__cardMeta) return null;

    const { id, side, solved } = button.__cardMeta;
    if (id == null || Number.isNaN(id)) return null;

    return { id, side, solved };
  }, []);

  const handlePointerDown = useCallback(
    (ev) => {
      const isCircle = ev.target.closest("[data-circle]");
      if (!isCircle) return;

      const cardInfo = getCardFromElement(ev.target);
      if (!cardInfo || cardInfo.solved || isNaN(cardInfo.id)) return;

      const position = getCirclePosition(cardInfo.id, cardInfo.side);
      if (!position) return;

      gameSounds.play(RELACIONAR_COLUMNAS_SOUNDS.ACTIVO);
      startDrag(cardInfo.id, cardInfo.side, position);

      if (cardInfo.side === "left") {
        selectLeft(cardInfo.id);
      } else {
        selectRight(cardInfo.id);
      }
    },
    [getCardFromElement, getCirclePosition, gameSounds, startDrag, selectLeft, selectRight]
  );

  const handlePointerMove = useCallback(
    (ev) => {
      if (!isDragging || !dragLine) return;

      const currentPos = {
        x: ev.clientX,
        y: ev.clientY,
      };

      const elementAtCursor = document.elementFromPoint(ev.clientX, ev.clientY);
      const cardInfo = getCardFromElement(elementAtCursor);

      if (cardInfo && !cardInfo.solved && cardInfo.side !== startCard.side) {
        const targetPosition = getCirclePosition(cardInfo.id, cardInfo.side);
        if (targetPosition) {
          updateDragLine(targetPosition);
          if (cardInfo.side === "left") {
            selectLeft(cardInfo.id);
          } else {
            selectRight(cardInfo.id);
          }
          return;
        }
      }

      updateDragLine(currentPos);
    },
    [isDragging, dragLine, startCard, getCardFromElement, getCirclePosition, updateDragLine, selectLeft, selectRight]
  );

  const handlePointerUp = useCallback(
    (ev) => {
      if (!isDragging || !startCard || !dragLine) return;

      const elementAtCursor = document.elementFromPoint(ev.clientX, ev.clientY);
      const cardInfo = getCardFromElement(elementAtCursor);

      if (
        cardInfo &&
        !cardInfo.solved &&
        cardInfo.side !== startCard.side &&
        !isNaN(cardInfo.id)
      ) {
        onDragMatch(startCard.id, startCard.side, cardInfo.id, cardInfo.side);
      } else {
        gameSounds.play(RELACIONAR_COLUMNAS_SOUNDS.MOVIENDO);
      }

      if (lineRef.current && getLength(dragLine.start, dragLine.end) > 0.0001) {
        const line = lineRef.current;
        const length = getLength(dragLine.start, dragLine.end);
        line.style.strokeDasharray = length;
        line.style.strokeDashoffset = 0;

        requestAnimationFrame(() => {
          line.style.transition = "stroke-dashoffset 0.1s linear";
          line.style.strokeDashoffset = length;
        });

        setTimeout(() => {
          endDrag();
          if (!cardInfo || cardInfo.solved || cardInfo.side === startCard.side) {
            clearSelection();
          }
        }, 100);
      } else {
        endDrag();
        if (!cardInfo || cardInfo.solved || cardInfo.side === startCard.side) {
          clearSelection();
        }
      }
    },
    [
      isDragging,
      startCard,
      dragLine,
      getCardFromElement,
      onDragMatch,
      gameSounds,
      endDrag,
      clearSelection,
    ]
  );

  useEffect(() => {
    if (!isDragging) return;

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isDragging, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    if (!isDragging || !startCard) return;

    const scrollContainer = containerRef.current;
    let scrollRafId = null;
    
    const handleScrollDuringDrag = () => {
      if (scrollRafId !== null) {
        cancelAnimationFrame(scrollRafId);
      }
      
      scrollRafId = requestAnimationFrame(() => {
        const startPosition = getCirclePosition(startCard.id, startCard.side);
        if (startPosition) {
          updateDragStart(startPosition);
        }
        scrollRafId = null;
      });
    };

    scrollContainer?.addEventListener('scroll', handleScrollDuringDrag, { passive: true });

    return () => {
      scrollContainer?.removeEventListener('scroll', handleScrollDuringDrag);
      if (scrollRafId !== null) {
        cancelAnimationFrame(scrollRafId);
      }
    };
  }, [isDragging, startCard, getCirclePosition, updateDragStart]);

  return (
    <div
      ref={canvasRef}
      className={styles.clickSpace}
      onPointerDown={handlePointerDown}
    >
      <div 
        className={styles.container} 
        ref={(node) => {
          containerRef.current = node;
          scrollContainerRef.current = node;
        }}
      >
        <div className={styles.content}>{children}</div>
      </div>
      <svg className={styles.canvas}>
        {isDragging && dragLine && (
          <>
            <line
              ref={lineRef}
              x1={dragLine.start.x}
              y1={dragLine.start.y}
              x2={dragLine.end.x}
              y2={dragLine.end.y}
              stroke="currentColor"
              strokeWidth={LINE_THICKNESS}
            />
            <circle
              cx={dragLine.start.x}
              cy={dragLine.start.y}
              r={CIRCLE_RADIUS}
              fill="currentColor"
            />
            <circle
              cx={dragLine.end.x}
              cy={dragLine.end.y}
              r={CIRCLE_RADIUS}
              fill="currentColor"
            />
          </>
        )}

        {arrows?.map((arrow) => (
          <AnimatedLine
            key={arrow.id}
            start={arrow.start}
            end={arrow.end}
          />
        ))}
      </svg>
    </div>
  );
}
